import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Calendar, 
  Users, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  TrendingUp,
  ArrowRight,
  MoreVertical,
  UserCheck,
  UserX,
  Ban,
  DollarSign,
  Scissors,
  Activity,
  Target
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Build last 12 months as { value: 'YYYY-MM', label: 'Octubre 2026' }
const buildMonthOptions = () => {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = format(d, "LLLL yyyy", { locale: es });
    options.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }
  return options;
};

const DashboardPage = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const monthOptions = buildMonthOptions();
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0].value);
  const [kpisLoading, setKpisLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, upcomingRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/dashboard/upcoming?limit=5`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setUpcoming(upcomingRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchKpis = useCallback(async (month) => {
    setKpisLoading(true);
    try {
      const kpisRes = await axios.get(`${API}/dashboard/kpis?month=${month}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKpis(kpisRes.data);
    } catch (error) {
      console.error('Error fetching KPIs:', error);
    } finally {
      setKpisLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchKpis(selectedMonth);
  }, [fetchKpis, selectedMonth]);

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      await axios.put(`${API}/appointments/${appointmentId}`, {
        status: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Refresh data after status change
      fetchData();
    } catch (error) {
      console.error('Error updating appointment status:', error);
      alert('Error al actualizar el estado de la cita');
    }
  };

  const statCards = [
    { 
      title: 'Citas Hoy', 
      value: stats?.today_appointments || 0, 
      icon: Calendar, 
      color: 'bg-[#0891B2]',
      textColor: 'text-[#0891B2]'
    },
    { 
      title: 'Pendientes', 
      value: stats?.pending_appointments || 0, 
      icon: Clock, 
      color: 'bg-amber-500',
      textColor: 'text-amber-500'
    },
    { 
      title: 'Confirmadas', 
      value: stats?.confirmed_appointments || 0, 
      icon: CheckCircle, 
      color: 'bg-emerald-500',
      textColor: 'text-emerald-500'
    },
    { 
      title: 'Canceladas', 
      value: stats?.cancelled_appointments || 0, 
      icon: XCircle, 
      color: 'bg-red-500',
      textColor: 'text-red-500'
    },
    { 
      title: 'No Asistieron', 
      value: stats?.no_show_appointments || 0, 
      icon: AlertCircle, 
      color: 'bg-slate-500',
      textColor: 'text-slate-500'
    },
    { 
      title: 'Total Pacientes', 
      value: stats?.total_patients || 0, 
      icon: Users, 
      color: 'bg-[#0B3B71]',
      textColor: 'text-[#0B3B71]'
    },
  ];

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'status-pending',
      confirmed: 'status-confirmed',
      attended: 'status-attended',
      cancelled: 'status-cancelled',
      no_show: 'status-no_show'
    };
    const labels = {
      pending: 'Pendiente',
      confirmed: 'Confirmada',
      attended: 'Atendida',
      cancelled: 'Cancelada',
      no_show: 'No asistió'
    };
    return <Badge className={styles[status]}>{labels[status]}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-dots flex gap-2">
          <span className="w-3 h-3 bg-[#0891B2] rounded-full"></span>
          <span className="w-3 h-3 bg-[#0891B2] rounded-full"></span>
          <span className="w-3 h-3 bg-[#0891B2] rounded-full"></span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-[#0B3B71]">Dashboard</h1>
        <p className="text-slate-500 mt-1">
          {format(new Date(), "EEEE, d 'de' MMMM 'de' yyyy", { locale: es })}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {statCards.map((stat, index) => (
          <Card key={index} className="card-hover border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="h-5 w-5 text-white" />
                </div>
                <TrendingUp className={`h-4 w-4 ${stat.textColor} opacity-50`} />
              </div>
              <div className="mt-3">
                <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                <p className="text-xs text-slate-500 mt-1">{stat.title}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Upcoming Appointments */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg font-semibold text-[#0B3B71]">
            Próximas Citas
          </CardTitle>
          <Link 
            to="/admin/calendar" 
            className="text-sm text-[#0891B2] hover:underline flex items-center gap-1"
          >
            Ver calendario <ArrowRight className="h-4 w-4" />
          </Link>
        </CardHeader>
        <CardContent>
          {upcoming.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Calendar className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No hay citas próximas</p>
            </div>
          ) : (
            <div className="space-y-3">
              {upcoming.map((appointment) => (
                <div
                  key={appointment.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                  data-testid={`appointment-${appointment.id}`}
                >
                  <Link
                    to={`/admin/appointments/${appointment.id}`}
                    className="flex items-center gap-4 flex-1"
                  >
                    <div className="w-12 h-12 rounded-lg bg-[#0891B2]/10 flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-[#0891B2]" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{appointment.patient_name}</p>
                      <p className="text-sm text-slate-500">
                        {format(new Date(appointment.appointment_date + 'T12:00:00'), "d MMM", { locale: es })} - {appointment.appointment_time}
                      </p>
                    </div>
                  </Link>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(appointment.status)}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white">
                        <DropdownMenuItem 
                          onClick={() => handleStatusChange(appointment.id, 'confirmed')}
                          className="cursor-pointer"
                        >
                          <CheckCircle className="h-4 w-4 mr-2 text-emerald-500" />
                          Confirmar
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleStatusChange(appointment.id, 'attended')}
                          className="cursor-pointer"
                        >
                          <UserCheck className="h-4 w-4 mr-2 text-blue-500" />
                          Marcar Atendida
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleStatusChange(appointment.id, 'no_show')}
                          className="cursor-pointer"
                        >
                          <UserX className="h-4 w-4 mr-2 text-slate-500" />
                          No Asistió
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleStatusChange(appointment.id, 'cancelled')}
                          className="cursor-pointer"
                        >
                          <Ban className="h-4 w-4 mr-2 text-red-500" />
                          Cancelar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Link to="/admin/calendar">
          <Card className="card-hover border-0 shadow-sm cursor-pointer">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#0891B2]/10 flex items-center justify-center">
                <Calendar className="h-6 w-6 text-[#0891B2]" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Ver Calendario</p>
                <p className="text-sm text-slate-500">Gestiona tu agenda</p>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/patients">
          <Card className="card-hover border-0 shadow-sm cursor-pointer">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#0B3B71]/10 flex items-center justify-center">
                <Users className="h-6 w-6 text-[#0B3B71]" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Pacientes</p>
                <p className="text-sm text-slate-500">Gestiona pacientes</p>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/admin/settings/availability">
          <Card className="card-hover border-0 shadow-sm cursor-pointer">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Clock className="h-6 w-6 text-emerald-500" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Disponibilidad</p>
                <p className="text-sm text-slate-500">Configura horarios</p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* KPIs Section */}
      {kpis && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-[#0B3B71]">KPIs del Mes</h2>
              <p className="text-xs text-slate-400">
                Los marcadores se reinician el primer día de cada mes
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Mes:</span>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-[180px]" data-testid="kpi-month-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value} data-testid={`kpi-month-option-${opt.value}`}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className={`grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4 transition-opacity ${kpisLoading ? 'opacity-50' : 'opacity-100'}`}>
            {/* Monthly Appointments Card */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Activity className="h-4 w-4 text-blue-500" />
                  Citas del Mes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Atendidas</span>
                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                      {kpis.monthly_appointments.attended}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Pendientes</span>
                    <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                      {kpis.monthly_appointments.pending}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Canceladas</span>
                    <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
                      {kpis.monthly_appointments.cancelled}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">No asistieron</span>
                    <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">
                      {kpis.monthly_appointments.no_show}
                    </Badge>
                  </div>
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between items-center font-semibold">
                      <span className="text-slate-700">Total</span>
                      <span className="text-[#0B3B71]">{kpis.monthly_appointments.total}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Clinical Activity Card */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Activity className="h-4 w-4 text-cyan-600" />
                  Actividad Clínica del Mes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Evaluaciones realizadas</span>
                    <Badge className="bg-cyan-100 text-cyan-700 hover:bg-cyan-100" data-testid="kpi-evaluations">
                      {kpis.clinical_activity?.evaluations ?? 0}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Pacientes evaluados</span>
                    <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100" data-testid="kpi-patients-evaluated">
                      {kpis.clinical_activity?.patients_evaluated ?? 0}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Agregados a CRM Cirugías</span>
                    <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100" data-testid="kpi-surgery-added">
                      {kpis.clinical_activity?.surgery_added ?? 0}
                    </Badge>
                  </div>
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between items-center font-semibold">
                      <span className="text-slate-700">Operados este mes</span>
                      <span className="text-purple-600" data-testid="kpi-surgery-operated">{kpis.clinical_activity?.surgery_operated ?? 0}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Surgery Conversion Funnel */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Target className="h-4 w-4 text-purple-500" />
                  Conversión Cirugías
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Pacientes evaluados</span>
                    <span className="font-semibold">{kpis.surgery_funnel.patients_evaluated}</span>
                  </div>
                  <div className="relative h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="absolute h-full bg-purple-300 rounded-full"
                      style={{ width: `${Math.min(100, kpis.surgery_funnel.eval_to_surgery_rate)}%` }}
                    />
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Agregados a CRM</span>
                    <div className="text-right">
                      <span className="font-semibold">{kpis.surgery_funnel.surgery_added ?? 0}</span>
                      <span className="text-xs text-slate-400 ml-1">({kpis.surgery_funnel.eval_to_surgery_rate}%)</span>
                    </div>
                  </div>
                  <div className="relative h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="absolute h-full bg-purple-500 rounded-full"
                      style={{ width: `${Math.min(100, kpis.surgery_funnel.surgery_to_operated_rate)}%` }}
                    />
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Operados este mes</span>
                    <div className="text-right">
                      <span className="font-semibold text-purple-600">{kpis.surgery_funnel.surgery_operated}</span>
                      <span className="text-xs text-slate-400 ml-1">({kpis.surgery_funnel.surgery_to_operated_rate}%)</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Revenue Card */}
            <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-white">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-emerald-600" />
                  Ingresos Estimados del Mes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Consultas</span>
                    <span className="font-medium">Q{kpis.revenue.consultations.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500">Cirugías</span>
                    <span className="font-medium">Q{kpis.revenue.surgeries.toLocaleString()}</span>
                  </div>
                  <div className="border-t pt-3 mt-2">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-slate-700">Total</span>
                      <span className="text-2xl font-bold text-emerald-600">
                        Q{kpis.revenue.total.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    * Basado en precios configurados por tipo de cita/cirugía
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
